import React, { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { X, AlertCircle, Info, Users, Shield, Mail, UserCheck, Sparkles } from 'lucide-react';
import { adminAPI } from '../../../../services/adminAPI';
import Button from '../../../../components/common/Button';
import Alert from '../../../../components/common/Alert';
import Tooltip from '../../../../components/common/Tooltip';
import { useOrganization } from '../../../../hooks/useOrganization';
import { supabase } from '../../../../config/supabase';

export default function EditUserModal({ isOpen, onClose, user, onSuccess }) {
  const queryClient = useQueryClient();
  const { organization } = useOrganization();
  
  // Initialize form state from the passed user prop.
  const [formData, setFormData] = useState({
    firstName: user?.first_name || (user?.name ? user.name.split(' ')[0] : ''),
    lastName:
      user?.last_name ||
      (user?.name && user.name.split(' ').length > 1
        ? user.name.split(' ').slice(1).join(' ')
        : ''),
    is_domain_admin: user?.is_domain_admin || false,
    has_seat: user?.has_seat || false,
    is_google_workspace_admin: false
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Get current Google Workspace admin
  const { data: orgDetails } = useQuery({
    queryKey: ['organizationDetails', organization?.id],
    queryFn: async () => {
      const response = await adminAPI.getOrganizationDetails();
      return response.data || {};
    },
    enabled: !!organization?.id && isOpen
  });

  // Update form state when the user prop changes.
  useEffect(() => {
    if (user) {
      const nameParts = user.name ? user.name.split(' ') : [];
      setFormData({
        firstName: user.first_name || (nameParts.length ? nameParts[0] : ''),
        lastName: user.last_name || (nameParts.length > 1 ? nameParts.slice(1).join(' ') : ''),
        is_domain_admin: user.is_domain_admin || false,
        has_seat: user.has_seat || false,
        is_google_workspace_admin: orgDetails?.google_workspace_admin_user_id === user.id
      });
    }
  }, [user, orgDetails]);
  
  // Focus on the first name input when the modal opens.
  const firstNameRef = useRef(null);
  useEffect(() => {
    if (isOpen && firstNameRef.current) {
      firstNameRef.current.focus();
    }
  }, [isOpen]);
  
  // Set up a mutation for updating the user
  const updateUserMutation = useMutation({
    mutationFn: async (payload) => {
      // Call the updated adminAPI.updateUser which sends the payload.
      const response = await adminAPI.updateUser(user.id, payload);
      return response;
    },
    onSuccess: async (data) => {
      setSuccessMessage('User details updated successfully');
      queryClient.invalidateQueries(['adminUsers']);
    },
    onError: (error) => {
      const msg =
        error.message ||
        error.response?.data?.message ||
        'Failed to update user details';
      setErrorMessage(msg);
    },
  });

  // Mutation for directly updating organization_members table (for admin role changes)
  const updateOrgMemberMutation = useMutation({
    mutationFn: async ({ userId, isAdmin }) => {
      try {
        // Use Supabase to directly update the role in organization_members
        const { data, error } = await supabase
          .from('organization_members')
          .update({ role: isAdmin ? 'admin' : 'member' })
          .eq('user_id', userId)
          .eq('organization_id', organization?.id);

        if (error) throw error;
        return data;
      } catch (error) {
        console.error('Failed to update member role:', error);
        throw error;
      }
    },
    onSuccess: () => {
      setSuccessMessage(prevMsg => {
        return prevMsg ? prevMsg + '. Admin role updated.' : 'Admin role updated successfully';
      });
      queryClient.invalidateQueries(['adminUsers']);
    },
    onError: (error) => {
      setErrorMessage('Failed to update admin role: ' + error.message);
    }
  });

  // Mutation for updating Google Workspace admin
  const updateGoogleAdminMutation = useMutation({
    mutationFn: async (userId) => {
      // Set this user as the Google Workspace admin
      return await adminAPI.setGoogleWorkspaceAdmin(userId);
    },
    onSuccess: () => {
      setSuccessMessage(prevMsg => {
        return prevMsg ? prevMsg + '. Google Workspace admin updated.' : 'Google Workspace admin updated successfully';
      });
      queryClient.invalidateQueries(['organizationDetails']);
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.message || 
        'Failed to set as Google Workspace admin';
      setErrorMessage(errorMsg);
    }
  });

  // Handle form submission.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setIsSubmitting(true);
    
    try {
      // Step 1: Update basic user details
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        name: formData.firstName + (formData.lastName ? ' ' + formData.lastName : ''),
        has_seat: formData.has_seat,
      };
      
      await updateUserMutation.mutateAsync(payload);
      
      // Step 2: Update admin role if changed
      const isCurrentlyAdmin = user.is_domain_admin;
      if (formData.is_domain_admin !== isCurrentlyAdmin) {
        await updateOrgMemberMutation.mutateAsync({
          userId: user.id,
          isAdmin: formData.is_domain_admin
        });
      }
      
      // Step 3: Update Google Workspace admin if changed
      const isCurrentGoogleAdmin = orgDetails?.google_workspace_admin_user_id === user.id;
      if (formData.is_google_workspace_admin && !isCurrentGoogleAdmin) {
        await updateGoogleAdminMutation.mutateAsync(user.id);
      }
      
      // Final success processing
      if (onSuccess) onSuccess();
      
      // If we have a success message, wait 1.5 seconds before closing for user to read it
      if (successMessage) {
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        onClose();
      }
    } catch (error) {
      console.error('Error in form submission:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen) return null;

  // Check if another user is already the Google Workspace admin
  const hasExistingGoogleAdmin = !!orgDetails?.google_workspace_admin_user_id && 
                               orgDetails.google_workspace_admin_user_id !== user.id;
  
  return (
    <div className="fixed inset-0 bg-midnight/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden transform transition-all">
        {/* Header with solid purple background */}
        <div className="bg-violet p-6 text-white">
          <div className="flex justify-between items-center">
            <h2 className="text-xl text-white font-bold flex items-center">
              <UserCheck className="w-6 h-6 mr-2" />
              Edit User Profile
            </h2>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors hover:bg-white/20 rounded-full p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {errorMessage && (
            <Alert type="error" className="mb-4">
              <AlertCircle className="w-4 h-4 mr-2" />
              {errorMessage}
            </Alert>
          )}
          
          {successMessage && (
            <Alert type="success" className="mb-4">
              <Sparkles className="w-4 h-4 mr-2" />
              {successMessage}
            </Alert>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email (read-only) */}
            <div className="relative">
              <label className="block text-sm font-medium text-midnight mb-1">Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Mail className="w-5 h-5 text-violet" />
                </div>
                <input
                  type="email"
                  value={user.email}
                  readOnly
                  className="w-full pl-10 px-4 py-2.5 rounded-xl border-2 border-light-purple bg-old-lace/30 text-ebony"
                />
              </div>
            </div>
            
            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-midnight mb-1">First Name</label>
              <input
                ref={firstNameRef}
                type="text"
                required
                value={formData.firstName}
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
                className="w-full px-4 py-2.5 rounded-xl border-2 border-light-purple focus:border-violet focus:ring-2 focus:ring-violet/20 transition-all"
                placeholder="Enter first name"
              />
            </div>
            
            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-midnight mb-1">Last Name</label>
              <input
                type="text"
                required
                value={formData.lastName}
                onChange={(e) =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
                className="w-full px-4 py-2.5 rounded-xl border-2 border-light-purple focus:border-violet focus:ring-2 focus:ring-violet/20 transition-all"
                placeholder="Enter last name"
              />
            </div>
            
            {/* Permission Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              {/* Seat Allocation Card */}
              <div className={`p-3 rounded-xl border-2 transition-colors ${formData.has_seat ? 'border-green-500 bg-green-50' : 'border-gray-200'} cursor-pointer`}
                onClick={() => setFormData(prev => ({ ...prev, has_seat: !prev.has_seat }))}>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.has_seat}
                    onChange={(e) => setFormData({ ...formData, has_seat: e.target.checked })}
                    className="rounded-full border-2 border-green-500 text-green-500 focus:ring-green-500 focus:ring-2"
                    id="hasSeatCheckbox"
                  />
                  <label htmlFor="hasSeatCheckbox" className="ml-2 flex items-center">
                    <Users className={`w-4 h-4 mr-1.5 ${formData.has_seat ? 'text-green-500' : 'text-gray-500'}`} />
                    <span className={`text-sm font-medium ${formData.has_seat ? 'text-green-700' : 'text-gray-700'}`}>Seat Allocation</span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1 ml-6">User can access the application</p>
              </div>
              
              {/* Admin Card */}
              <div className={`p-3 rounded-xl border-2 transition-colors ${formData.is_domain_admin ? 'border-purple-500 bg-purple-50' : 'border-gray-200'} cursor-pointer`}
                onClick={() => setFormData(prev => ({ ...prev, is_domain_admin: !prev.is_domain_admin }))}>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_domain_admin}
                    onChange={(e) => setFormData({ ...formData, is_domain_admin: e.target.checked })}
                    className="rounded-full border-2 border-purple-500 text-purple-500 focus:ring-purple-500 focus:ring-2"
                    id="domainAdminCheckbox"
                  />
                  <label htmlFor="domainAdminCheckbox" className="ml-2 flex items-center">
                    <Shield className={`w-4 h-4 mr-1.5 ${formData.is_domain_admin ? 'text-purple-500' : 'text-gray-500'}`} />
                    <span className={`text-sm font-medium ${formData.is_domain_admin ? 'text-purple-700' : 'text-gray-700'}`}>Admin Access</span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1 ml-6">Can manage users and settings</p>
              </div>
            </div>

            {/* Google Workspace Admin Section */}
<div className={`p-4 rounded-xl border-2 transition-colors ${formData.is_google_workspace_admin ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
  <div className="flex items-start">
    {/* Checkbox and label */}
    <div 
      className="flex items-center cursor-pointer flex-1"
      onClick={() => setFormData(prev => ({ ...prev, is_google_workspace_admin: !prev.is_google_workspace_admin }))}
    >
      <input
        type="checkbox"
        checked={formData.is_google_workspace_admin}
        onChange={(e) => setFormData({ ...formData, is_google_workspace_admin: e.target.checked })}
        className="rounded-full border-2 border-blue-500 text-blue-500 focus:ring-blue-500 focus:ring-2 mt-0.5"
        id="googleWorkspaceAdminCheckbox"
      />
      <div className="ml-2">
        <div className="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className={`w-5 h-5 mr-1.5 ${formData.is_google_workspace_admin ? 'text-blue-500' : 'text-gray-500'}`} viewBox="0 0 24 24" fill="currentColor">
            <path d="M22 12.727c0 1.773-1.45 3.273-3.182 3.273h-7.636v-6.545h7.636c1.732 0 3.182 1.5 3.182 3.272zm-14.727-7.09c-1.45 0-2.637-1.23-2.637-2.728 0-1.5 1.186-2.727 2.637-2.727s2.637 1.227 2.637 2.727c0 1.5-1.186 2.727-2.637 2.727zm0 14.18c-1.45 0-2.637-1.227-2.637-2.726 0-1.5 1.186-2.727 2.637-2.727s2.637 1.227 2.637 2.727c0 1.5-1.186 2.727-2.637 2.727zM1.363 12.727c0 1.773 1.45 3.273 3.183 3.273h7.636v-6.545H4.546c-1.732 0-3.183 1.5-3.183 3.272z" />
          </svg>
          <span className={`text-sm font-medium ${formData.is_google_workspace_admin ? 'text-blue-700' : 'text-gray-700'}`}>
            Google Workspace Admin
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Can manage organization's Google Workspace settings and groups
        </p>
      </div>
    </div>
    
    {/* Info icon with custom tooltip implementation */}
    <div className="ml-2 relative group">
      <div className="p-1 hover:bg-gray-100 rounded-full cursor-help">
        <Info className="w-4 h-4 text-gray-500" />
      </div>
      
      {/* Custom tooltip that's absolutely positioned to the document */}
      <div 
        className="fixed opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-[9999] pointer-events-none"
        style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, 120%)',
          maxWidth: '250px'
        }}
      >
        <div className="bg-midnight text-white text-xs rounded py-2 px-3 shadow-lg">
          {hasExistingGoogleAdmin 
            ? "Another user is already set as Google Workspace admin. Changing this will replace them." 
            : "This user will be able to manage Google Workspace groups and settings for the organization."
          }
          <div className="absolute w-2 h-2 bg-midnight transform rotate-45 left-[90%] bottom-[-4px]"></div>
        </div>
      </div>
    </div>
  </div>
  
  {/* Warning alert for replacing existing admin */}
  {hasExistingGoogleAdmin && formData.is_google_workspace_admin && (
    <Alert type="warning" className="text-xs mt-3">
      <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" />
      This will replace the current Google Workspace admin.
    </Alert>
  )}
</div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <Button 
                variant="outline" 
                onClick={onClose} 
                disabled={isSubmitting}
                className="rounded-xl border-2 px-5"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="rounded-xl px-5 bg-violet hover:bg-violet/90"
              >
                {isSubmitting ? 
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </span> 
                  : 'Save Changes'
                }
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}